import { create } from 'zustand';
import { fetchReleaseNotesIncremental } from 'src/services/releaseNotesService';
import { ReleaseNote } from 'src/components/WhatsNew/WhatsNew';

const WHATS_NEW_SEEN_KEY = 'snow_whats_new_seen_version';

interface ReleaseNotesState {
	releaseNotes: ReleaseNote[];
	currentVersion: string;
	isLoading: boolean;
	error: string | null;
	hasFetched: boolean;
	hasUnseenUpdates: boolean;
	pendingMarkAsSeen: boolean;

	fetchIfNeeded: (markAsSeen?: boolean) => void;
	markAsSeen: () => void;
}

export const useReleaseNotesStore = create<ReleaseNotesState>((set, get) => ({
	releaseNotes: [],
	currentVersion: '',
	isLoading: false,
	error: null,
	hasFetched: false,
	hasUnseenUpdates: false,
	pendingMarkAsSeen: false,

	fetchIfNeeded: (markAsSeen = false) => {
		if (get().hasFetched) {
			if (markAsSeen) get().markAsSeen();
			return;
		}
		if (get().isLoading) {
			if (markAsSeen) set({ pendingMarkAsSeen: true });
			return;
		}

		set({ isLoading: true, error: null });

		fetchReleaseNotesIncremental(
			(notes, version) => {
				const shouldMarkAsSeen = markAsSeen || get().pendingMarkAsSeen;
				if (shouldMarkAsSeen && version) {
					localStorage.setItem(WHATS_NEW_SEEN_KEY, version);
				}
				const seenVersion = localStorage.getItem(WHATS_NEW_SEEN_KEY);
				set({
					releaseNotes: notes,
					currentVersion: version,
					hasUnseenUpdates: !!version && seenVersion !== version,
					pendingMarkAsSeen: false,
				});
			},
			() => set({ isLoading: false, hasFetched: true }),
		).catch((err) => {
			set({
				error: err instanceof Error ? err.message : 'Failed to load release notes',
				isLoading: false,
				hasFetched: true,
				pendingMarkAsSeen: false,
			});
		});
	},

	markAsSeen: () => {
		const { currentVersion } = get();
		if (currentVersion) {
			localStorage.setItem(WHATS_NEW_SEEN_KEY, currentVersion);
		}
		set({ hasUnseenUpdates: false });
	},
}));
